import { useState, useCallback } from 'react';
import { createNotificationApi, getNotificationApi, deleteNotificationApi } from 'api/client';
import { tryCatch } from 'api/utils';
import {
    NOTIFY_DELIVER_TYPE_MAP,
    NOTIFY_SOURCE_TYPE_MAP,
    NOTIFY_TITLE_MAP,
    NOTIFY_STATUS_TYPE_MAP
} from 'config/notifyTypes';
import useClientErrorHandler from 'client/hooks/useClientErrorHandler';
import * as clientErrTypes from 'config/clientErrorTypes';

const checkNotifyStatus = notifyObj => Object.values(notifyObj).every(obj => obj === null);

const mappingNotifyApiData = (notifyObj, notifyConfigArray = []) => {
    if (Boolean(notifyObj) && Object.keys(notifyObj).length > 0) {
        return notifyConfigArray.reduce((result, item = {}) => {
            if (Boolean(item?.list) && item.list.length > 0) {
                item.list = item.list.map(data => {
                    const aNotify = notifyObj[data.type];
                    if (aNotify) {
                        item.description = aNotify?.target || null;
                        if (data.type === NOTIFY_DELIVER_TYPE_MAP.EMAIL) {
                            item.canEdit = true;
                        }
                    }

                    return {
                        ...data,
                        ...{
                            // label 用來當成知道自已是否在 active 中，所以會把 list id 塞到 每個 list item 的 Label
                            label: Boolean(aNotify?.status === NOTIFY_STATUS_TYPE_MAP.ACTIVE)
                                ? item.id
                                : '',
                            value: aNotify?.target,
                            source: aNotify?.source_type,
                            status: aNotify?.status
                        }
                    };
                });
                return [...result, item];
            }
            return [...result, item];
        }, []);
    } else return [];
};

const useGetNotifyData = ({ promoUUID }) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const errHandler = useClientErrorHandler();

    const initNotifyConfig = (titleObj, typeObj) => {
        return Object.keys(typeObj).map((type, index) => ({
            id: index,
            title: titleObj[type],
            description: '',
            list: [
                {
                    label: '',
                    type: typeObj[type],
                    disabled: false,
                    canEdit: false
                }
            ]
        }));
    };

    /**
     * type (string): 通知方式 (Email 通知: messageBirdEmail | Line 通知: lineNotify)
     * source (number): 後端紀錄 Email 通知的類型 (1: 來自後台的 email | 2: user 參加活動登入後的 email | 3: user 參加活動後填表填寫的 email | 4: user 手動編輯的 email)
     * value (string):  email address (Ex: 'test@gmail.com') | code (line notify api 回傳的 code Ex: '123456')
     */
    const postNotification = useCallback(
        async ({ type, source, value }) => {
            const payload = {
                promoUUID,
                sourceType:
                    type === NOTIFY_DELIVER_TYPE_MAP.LINE
                        ? NOTIFY_SOURCE_TYPE_MAP.MANUAL_INPUT
                        : source,
                driver: type,
                code: value,
                email: value
            };
            try {
                await createNotificationApi(payload);
            } catch (error) {
                if (error.message === clientErrTypes.NOTIFY_CREATE_FAILED) {
                    errHandler({ err: error });
                }
                return false;
            }
        },
        [errHandler, promoUUID]
    );

    const delateNotification = useCallback(async () => {
        const [error] = await tryCatch(deleteNotificationApi(promoUUID));
        if (error) {
            errHandler({ err: error });
            return false;
        }
        return true;
    }, [errHandler, promoUUID]);

    const getNotification = useCallback(async () => {
        setIsLoading(true);
        const [error, resp] = await tryCatch(getNotificationApi(promoUUID));
        if (error) {
            errHandler({ err: error });
            setData([]);
            setIsLoading(false);
            return;
        }
        const { payload } = resp;
        // 如果此 member 沒有任何通知設定，則需要先建立通知設定 (沒有 messageBirdEmail 的通知設定，也沒有 Line Notify 的通知設定)
        const haveNotInitNotifySetting = checkNotifyStatus(payload);
        if (haveNotInitNotifySetting) {
            // 自動幫 member 建立通知設定 (預設是 messageBirdEmail 的通知設定)，email 預設是 member 參加活動登入後的 email，後端會自己取，不需要前端帶
            const isSuccess = await postNotification({
                type: NOTIFY_DELIVER_TYPE_MAP.EMAIL,
                source: NOTIFY_SOURCE_TYPE_MAP.LOGIN_EMAIL
            });
            if (!isSuccess) return;
        }
        const listInitData = initNotifyConfig(NOTIFY_TITLE_MAP, NOTIFY_DELIVER_TYPE_MAP);
        const mappingNotifyList = mappingNotifyApiData(payload, listInitData);
        setData(mappingNotifyList);
        setIsLoading(false);
    }, [errHandler, postNotification, promoUUID]);

    return {
        data,
        isLoading,
        postNotification,
        delateNotification,
        getNotification
    };
};

export default useGetNotifyData;
